import React, {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Dimensions, ListRenderItem, Platform, View} from 'react-native';
import {FlatList} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {zodResolver} from '@hookform/resolvers/zod';
import {Icon} from '@rneui/themed';
import {observer} from 'mobx-react';

import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {SlotFormData, slotSchema} from '../../../schemas/checkoutSchemas';
import {CartStore} from '../../../stores/cartStore';
import {useStores} from '../../../stores/use-stores';
import {Day} from '../../../transport/apiServiceResponse';
import {visibleScrollbarProps} from '../../../utils/webScrollbar';
import ActivityIndicator from '../../themed/ActivityIndicator';
import Button from '../../themed/Button';
import Text from '../../themed/Text';
import ValidationMessage from '../../themed/ValidationMessage';
import CrossPlatformModal from '../../ui-controls/CrossPlatformModal';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';

import DayTile, {TimeTile, TimeTileSpacer} from './DayTile';

const PICKUP_SUBTITLE = `Pick up times have a 30 minute window`;
const DELIVERY_SUBTITLE = `Delivery times have a 1 hour window`;

function SlotModal({
  children,
  methodTouched,
}: {
  /**
   * if delivery method switch is touched and selected slot empty then show modal
   */
  methodTouched: boolean;
  children: ({handleOpen}: {handleOpen: () => void}) => React.ReactNode;
}) {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const {cartStore} = useStores();

  const onClose = () => setIsVisible(false);
  const handleOpen = () => setIsVisible(true);

  const selectDayText = cartStore.isDelivery
    ? 'Select a day for delivery'
    : 'Select a day for pick up';
  const subtitle = cartStore.isDelivery ? DELIVERY_SUBTITLE : PICKUP_SUBTITLE;

  // Setup React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: {errors, touchedFields},
  } = useForm<SlotFormData>({
    mode: 'onTouched',
    resolver: zodResolver(slotSchema),
    defaultValues: {
      selectedDay: cartStore.selectedDay || cartStore.days?.[0] || null,
      selectedSlot: cartStore.selectedSlot,
    },
  });

  // Watch form values for reactive updates
  const selectedDayValue = watch('selectedDay');
  const selectedSlotValue = watch('selectedSlot');

  // when delivery method is changed and no delivery slot show the slot modal
  useEffect(() => {
    if (!cartStore.selectedSlot && methodTouched) {
      setIsVisible(true);
    }
  }, [cartStore, cartStore.selectSlot, methodTouched]);

  // Reset form when modal opens
  useEffect(() => {
    if (isVisible) {
      reset({
        selectedDay: cartStore.selectedDay || cartStore.days?.[0] || null,
        selectedSlot: cartStore.selectedSlot,
      });
    }
  }, [isVisible, cartStore.selectedDay, cartStore.days, cartStore.selectedSlot, reset]);

  // Handler functions using setValue
  const handleDayChange = (day: Day) => {
    setValue('selectedDay', day, {shouldTouch: true});
    setValue('selectedSlot', null, {shouldTouch: true}); // Clear slot when day changes
  };

  const handleSlotChange = (slot: CartStore['selectedSlot']) => {
    setValue('selectedSlot', slot, {shouldTouch: true});
  };

  // Submit handler
  const onSubmit = (data: SlotFormData) => {
    setSubmitting(true);
    /**
     * set day
     */
    cartStore.selectDay(data.selectedDay);

    /**
     * set slot
     */
    cartStore.selectSlot(data.selectedSlot, () => {
      setSubmitting(false);
      onClose?.();
    });
  };

  const insets = useSafeAreaInsets();

  return (
    <>
      {children({handleOpen})}
      <CrossPlatformModal
        isVisible={isVisible}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={onClose}
        onBackButtonPress={onClose}
        style={{padding: 0, margin: 0}}>
        <View
          style={{
            paddingTop: insets.top,
            // DIS-1716: the modal is sized with 'screen' height below, so its
            // surface covers the Android nav bar as well as the iOS home
            // indicator. Both platforms need the inset or the content clips.
            paddingBottom: insets.bottom,
            // Use 'screen' height (includes nav bar) so modal fills the entire
            // overlay — 'window' height excludes the nav bar, creating a gap at
            // the bottom where the underlying screen bleeds through.
            maxHeight: Dimensions.get('screen').height,
            maxWidth: Dimensions.get('screen').width,
            backgroundColor: 'white',
            // Native modal fills the screen, so flex:1 gives a bounded height
            // for the time-slot list to scroll within. On web the modal wrapper
            // shrink-wraps to content, so flex:1 never resolves to a bounded
            // height — pin the viewport height so the list scrolls and the SAVE
            // TIMES footer stays visible regardless of slot count.
            ...(Platform.OS === 'web' ? {height: Dimensions.get('window').height} : {flex: 1}),
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: 20,
            }}>
            <Text style={{marginTop: 24, marginBottom: 18}} variant="heading1">
              {' ' /* put title here if needed */}
            </Text>
            <PressableWithOpacityFeedback onPress={onClose}>
              <Icon name="close" size={30} />
            </PressableWithOpacityFeedback>
          </View>

          {cartStore.isDelivery && cartStore.noDeliverySlotsAvailable ? (
            <View style={{padding: 20}}>
              <Empty
                noLoading
                message="Delivery is only available within a 5km radius of this store.  Please update your delivery address to see availability, otherwise select Pick Up."
              />
            </View>
          ) : (
            <>
              <View style={{padding: 20, flex: 1}}>
                <Text center style={{marginBottom: 20}} color="black" variant="heading3">
                  {selectDayText}
                </Text>
                <Controller
                  control={control}
                  name="selectedDay"
                  render={() => (
                    <>
                      <ChooseDay value={selectedDayValue} onChange={handleDayChange} />
                      <ValidationMessage>
                        {!!touchedFields.selectedDay && errors.selectedDay?.message}
                      </ValidationMessage>
                    </>
                  )}
                />
                <View style={{flex: 1, flexDirection: 'column'}}>
                  <Text
                    style={{marginTop: 40, marginBottom: 8}}
                    center
                    color="black"
                    variant="heading3">
                    Select a time
                  </Text>
                  <Text style={{marginBottom: 20}} center variant="caption">
                    {subtitle}
                  </Text>
                  <Controller
                    control={control}
                    name="selectedSlot"
                    render={() => (
                      <>
                        <ChooseTime
                          selectedDay={selectedDayValue}
                          value={selectedSlotValue}
                          onChange={handleSlotChange}
                        />
                        <ValidationMessage>
                          {!!touchedFields.selectedSlot && errors.selectedSlot?.message}
                        </ValidationMessage>
                      </>
                    )}
                  />
                </View>
              </View>
              <View style={{padding: 20}}>
                <Button
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onPress={handleSubmit(onSubmit)}>
                  SAVE TIMES
                </Button>
              </View>
            </>
          )}
        </View>
      </CrossPlatformModal>
    </>
  );
}

function ItemSeparator() {
  return <View style={{width: 8, height: 8}} />;
}

type ChooseDayProps = {
  value: Day | null;
  onChange: (day: string) => void;
};

const ChooseDay = observer(({value, onChange}: ChooseDayProps) => {
  const {cartStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const isLoading = cartStore.isDelivery
    ? cartStore.loadingDeliverySlots
    : cartStore.loadingPickupSlots;

  const renderDayItem: ListRenderItem<Day> = ({item}) => {
    return <DayTile selected={item === value} onPress={onChange} item={item} />;
  };

  return isLoading ? (
    <Empty />
  ) : (
    <FlatList
      style={{flexGrow: 0}}
      data={cartStore.days || []}
      renderItem={renderDayItem}
      horizontal
      ItemSeparatorComponent={ItemSeparator}
      extraData={value}
      ListEmptyComponent={Empty}
      scrollEnabled
      showsHorizontalScrollIndicator={isWebDesktopScreen}
      {...visibleScrollbarProps(isWebDesktopScreen)}
    />
  );
});

type ChooseTimeProps = {
  value: CartStore['selectedSlot'];
  onChange: (slot: CartStore['selectedSlot']) => void;
  selectedDay: Day | null;
};

const ChooseTime = observer(({value, onChange, selectedDay}: ChooseTimeProps) => {
  const {cartStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();
  if (!selectedDay) {
    return <Empty message="Please select a day" />;
  }

  const times =
    (cartStore.isDelivery
      ? cartStore.deliverySlots[selectedDay]
      : cartStore.pickupSlots[selectedDay]) ?? [];

  const isLoading = cartStore.isDelivery
    ? cartStore.loadingDeliverySlots
    : cartStore.loadingPickupSlots;

  const numColumns = 3;

  const numSpacerTiles = Math.ceil(times.length / numColumns) * numColumns - times.length;

  const spacerTiles = Array.from({length: numSpacerTiles}, () => ({
    spacerTile: true,
  }));

  return times?.length === 0 || isLoading ? (
    <Empty message="No times available" />
  ) : (
    <FlatList
      style={{flex: 1, marginHorizontal: -13}}
      scrollEnabled
      showsVerticalScrollIndicator={isWebDesktopScreen}
      data={[...times, ...spacerTiles]}
      numColumns={numColumns}
      renderItem={({item, index}) =>
        item?.spacerTile ? (
          <TimeTileSpacer />
        ) : (
          <TimeTile
            selected={value?.id === item?.id}
            key={item?.id}
            item={item}
            onPress={onChange}
            testID={`time-slot-${index}`}
          />
        )
      }
    />
  );
});

/**
 * Prop default are suitable for choose day flatlist
 * will either show activity loader, message. will only show message if noLoading prop is used
 */
const Empty = observer(
  ({
    message = 'No days available',
    noLoading,
  }: {
    message?: string;
    /**
     * never show the loading indicator. only show the message
     */
    noLoading?: boolean;
  }) => {
    const {cartStore} = useStores();

    const isLoading = cartStore.isDelivery
      ? cartStore.loadingDeliverySlots
      : cartStore.loadingPickupSlots;

    return (
      <View
        style={{
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'center',
        }}>
        {noLoading || !isLoading ? (
          <Text center>{message}</Text>
        ) : isLoading ? (
          <ActivityIndicator />
        ) : null}
      </View>
    );
  },
);

export default observer(SlotModal);
